import { useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import fish from '~/assets/icons/fish.svg';
import fishingIcon from '~/assets/icons/fishing_2.svg';
import info from '~/assets/icons/info_2.svg';
import weightIcon from '~/assets/icons/weight.svg';
import { CollectionImage } from '~/components/collections/CollectionImage';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { rage } from '~/rage';
import { FishRarity } from '~/shared';
import {
	StyledButtonContent,
	StyledButtonsContainer,
	StyledContainer,
	StyledExpContainer,
	StyledHint,
	StyledHintButtonContainer,
	StyledImageContainer,
	StyledProgress,
	StyledProgressContainer,
	StyledProgressWrapper,
	StyledProgressXPContainer,
	StyledShine,
	StyledTag,
	StyledTagsContainer,
	StyledTitle,
	StyledWeightContainer,
	StyledWrapper,
} from './FinishModal.styled';

const fishTypeNames = {
	[FishRarity.COMMON]: 'Обычная рыба',
	[FishRarity.TROPHY]: 'Трофейная рыба',
	[FishRarity.RARE]: 'Редкая рыба',
	[FishRarity.FORBIDDEN]: 'Запрещенная рыба',
};

const FinishModal = ({
	fishId,
	fishType,
	level,
	maxLevel,
	levelExp,
	weight,
	exp,
	expRelease,
	name,
	description,
	nextLevelExp,
	handleClose,
}: {
	fishId: string;
	fishType: FishRarity;
	level: number;
	maxLevel: number;
	levelExp: number;
	weight: number;
	exp: number;
	expRelease: number;
	name: string;
	description: string;
	nextLevelExp: number;
	handleClose: () => void;
}) => {
	const [hintVisible, setHintVisible] = useState(false);

	const isMaxLevel = maxLevel > 0 ? level >= maxLevel : !nextLevelExp;
	const progress =
		isMaxLevel || !nextLevelExp
			? 1
			: Math.min(Math.max(levelExp / nextLevelExp, 0), 1);

	const handleRelease = () => {
		rage.gameFishing
			.releaseFish(fishId)
			.catch((err) => rage.notifications.showException(err));
		handleClose();
	};

	return (
		<StyledWrapper>
			<StyledContainer>
				<StyledTitle>
					<img src={fishingIcon} />
					<Text>Вы поймали рыбу</Text>
				</StyledTitle>
				<StyledImageContainer>
					<Text>{name}</Text>
					<StyledTagsContainer>
						<StyledExpContainer>
							<img src={fish} />
							<Text>+{exp} EXP</Text>
						</StyledExpContainer>
						<StyledTag className={fishType}>
							<StyledShine />
							<Text>{fishTypeNames[fishType]}</Text>
						</StyledTag>
					</StyledTagsContainer>
					<CollectionImage collection="fish" id={fishId} size="lg" />
					<StyledWeightContainer>
						<img src={weightIcon} />
						<Text>{weight} кг.</Text>
					</StyledWeightContainer>
					<StyledHintButtonContainer>
						<Button
							color={ButtonColors.Default}
							height={ButtonHeight.Height20}
							borderRadius={ButtonBorderRadius.Radius5}
							onMouseEnter={() => setHintVisible(true)}
							onMouseLeave={() => setHintVisible(false)}
						>
							<img src={info} width={10} height={10} />
						</Button>
					</StyledHintButtonContainer>
					<CSSTransition
						in={hintVisible}
						timeout={300}
						classNames="hint"
						unmountOnExit
					>
						<StyledHint>
							<Text>{name}</Text>
							<Text>{description}</Text>
						</StyledHint>
					</CSSTransition>
				</StyledImageContainer>
				<StyledProgressWrapper>
					<StyledProgressContainer>
						<StyledProgress style={{ width: `${progress * 100}%` }} />
					</StyledProgressContainer>
					<Text>{level} уровень</Text>
					<StyledProgressXPContainer>
						{!isMaxLevel && (
							<>
								<img src={fish} />
								<Text>
									<span>{levelExp?.toLocaleString('ru-RU')}</span> /{' '}
									{nextLevelExp?.toLocaleString('ru-RU')} EXP
								</Text>
							</>
						)}
					</StyledProgressXPContainer>
					<Text style={{ fontWeight: 500, textAlign: 'right' }}>
						{isMaxLevel ? 'Макс.' : `${level + 1} уровень`}
					</Text>
				</StyledProgressWrapper>
				<StyledButtonsContainer>
					<Button
						color={ButtonColors.Default}
						height={ButtonHeight.Height50}
						style={{ fontSize: 16 }}
						onClick={handleClose}
					>
						<Text>Забрать рыбу</Text>
					</Button>
					<Button
						color={ButtonColors.Select2}
						height={ButtonHeight.Height50}
						style={{ fontSize: 16 }}
						onClick={handleRelease}
					>
						<StyledButtonContent>
							<Text>Отпустить</Text>
							<Text style={{ fontWeight: 500 }}>+ {expRelease} EXP</Text>
						</StyledButtonContent>
					</Button>
				</StyledButtonsContainer>
			</StyledContainer>
		</StyledWrapper>
	);
};

export { FinishModal };
